import { AuthContext } from '@repo-level/core-front';
import type { IMissionCard } from 'api/cms/missions';
import { BonusStage } from 'api/common';
import type { TMissionsProgressResponse } from 'api/missions';
import { RealTimeServiceContext } from 'contexts/realTimeService';
import { useContext, useMemo } from 'react';
import { MAX_PROGRESS } from '../constants';
import {
  getIsMissionDone,
  getMissionRewardType,
  getMissionStatus,
  isBonusInactive,
  isCompletedByGroup,
} from '../helpers/mission';
import { MissionRewardType } from '../types';

export const useMissionInfo = (
  mission: IMissionCard,
  progress?: TMissionsProgressResponse
) => {
  const { user } = useContext(AuthContext);
  const { freespins, bonuses } = useContext(RealTimeServiceContext);

  const { groupKeyId, backendId, prizeType } = mission;

  const missionProgress = useMemo(() => {
    const raw = progress?.[backendId ?? '']?.progress ?? 0;
    return isCompletedByGroup(backendId, user) ? MAX_PROGRESS : raw;
  }, [progress, backendId, user]);

  const rewardType = useMemo(
    () => getMissionRewardType(backendId, prizeType),
    [backendId, prizeType]
  );

  const bonusStage = useMemo((): BonusStage | undefined => {
    if (!groupKeyId) return;

    const freespin = freespins.find((item) => item.groupKey === groupKeyId);
    if (freespin) return freespin.stage;

    const bonus = bonuses.find((item) => item.groupKey === groupKeyId);
    return bonus?.stage;
  }, [freespins, bonuses, groupKeyId]);

  const isMissionDone = useMemo(() => {
    if (rewardType === MissionRewardType.FREE_SPINS) {
      return (
        getIsMissionDone(backendId, user, missionProgress) ||
        bonusStage === BonusStage.Activated ||
        bonusStage === BonusStage.HandleBets ||
        isBonusInactive(bonusStage)
      );
    }

    if (rewardType === MissionRewardType.CASH_BONUS) {
      return (
        bonusStage !== undefined ||
        getIsMissionDone(backendId, user, missionProgress)
      );
    }

    return getIsMissionDone(backendId, user, missionProgress);
  }, [backendId, user, missionProgress, rewardType, bonusStage]);

  const missionStatus = useMemo(
    () => getMissionStatus(isMissionDone, missionProgress),
    [isMissionDone, missionProgress]
  );

  const isRewardInactive = useMemo(
    () => isBonusInactive(bonusStage),
    [bonusStage]
  );

  return {
    missionStatus,
    missionProgress,
    bonusStage,
    isMissionDone,
    isRewardInactive,
    rewardType,
  };
};
